/**
 * `++text++` is what the Markdown editor's underline mark serializes to. The delimiter rule is
 * what stops two unrelated `++` runs — `Requires C++ and C++17.` — from being joined into one
 * underline: the opening `++` must not be followed by whitespace, and the closing `++` must not
 * be preceded by any. The editor's tokenizer and the render-side plugin below both build on this
 * one source so they can never disagree about what is underlined.
 */
export const markdownUnderlineSource = '\\+\\+(?!\\s)([\\s\\S]*?[^\\s])\\+\\+';

/** Matches an underline only at the start of the string, for the editor's inline tokenizer. */
export const markdownUnderlinePattern = new RegExp(`^${markdownUnderlineSource}`);

type MarkdownUnderlineNode = {
  type: string;
  value?: string;
  children?: MarkdownUnderlineNode[];
  data?: Record<string, unknown>;
  position?: { start: { offset?: number }; end: { offset?: number } };
};

/** Stands in for a `+` that the source escaped, so the underline pattern cannot match it. */
const ESCAPED_PLUS = '\u0000';

const ASCII_PUNCTUATION = /[!-/:-@[-`{-~]/;

/**
 * The parser removes backslash escapes and decodes entities before this plugin sees a text node,
 * so `\+\+b\+\+` arrives as `++b++`. This walks the node's source next to its value and returns
 * the value with every escaped or entity-written `+` replaced by a placeholder. When the two
 * cannot be lined up, the value is returned unchanged.
 */
function maskEscapedPluses(value: string, source: string): string {
  const masked = value.split('');
  let sourceIndex = 0;
  let valueIndex = 0;

  while (sourceIndex < source.length && valueIndex < value.length) {
    const char = source[sourceIndex];
    const next = source[sourceIndex + 1];

    if (char === '\\' && next !== undefined && ASCII_PUNCTUATION.test(next)) {
      if (value[valueIndex] !== next) {
        return value;
      }

      if (next === '+') {
        masked[valueIndex] = ESCAPED_PLUS;
      }

      sourceIndex += 2;
      valueIndex += 1;
    } else if (char === '&' && /^&(?:#\d+|#[xX][\da-fA-F]+|\w+);/.test(source.slice(sourceIndex))) {
      if (value[valueIndex] === '+') {
        masked[valueIndex] = ESCAPED_PLUS;
      }

      sourceIndex = source.indexOf(';', sourceIndex) + 1;
      valueIndex += 1;
    } else if (char === value[valueIndex]) {
      sourceIndex += 1;
      valueIndex += 1;
    } else if (/\s/.test(char)) {
      // Whitespace the parser trimmed, such as the indent of a continuation line.
      sourceIndex += 1;
    } else {
      return value;
    }
  }

  return masked.join('');
}

function splitUnderlines(value: string, source: string | null): MarkdownUnderlineNode[] | null {
  const pattern = new RegExp(markdownUnderlineSource, 'g');
  const searchable =
    source !== null && value.includes('++') ? maskEscapedPluses(value, source) : value;
  const nodes: MarkdownUnderlineNode[] = [];
  let lastIndex = 0;
  let match = pattern.exec(searchable);

  while (match !== null) {
    if (match.index > lastIndex) {
      nodes.push({ type: 'text', value: value.slice(lastIndex, match.index) });
    }

    nodes.push({
      type: 'underline',
      // mdast has no underline node, so `hName` is how mdast-util-to-hast is told to emit a
      // `<u>` element without opening the render path up to raw HTML.
      data: { hName: 'u' },
      children: [
        { type: 'text', value: value.slice(match.index + 2, match.index + match[0].length - 2) }
      ]
    });

    lastIndex = match.index + match[0].length;
    match = pattern.exec(searchable);
  }

  if (nodes.length === 0) {
    return null;
  }

  if (lastIndex < value.length) {
    nodes.push({ type: 'text', value: value.slice(lastIndex) });
  }

  return nodes;
}

function sourceOf(child: MarkdownUnderlineNode, fileSource: string): string | null {
  const start = child.position?.start.offset;
  const end = child.position?.end.offset;

  return start === undefined || end === undefined ? null : fileSource.slice(start, end);
}

function replaceUnderlines(node: MarkdownUnderlineNode, fileSource: string): void {
  const children = node.children;

  if (!children) {
    return;
  }

  node.children = children.flatMap((child) => {
    if (child.type !== 'text') {
      replaceUnderlines(child, fileSource);
      return child;
    }

    return splitUnderlines(child.value ?? '', sourceOf(child, fileSource)) ?? child;
  });
}

/**
 * A remark plugin that renders `++text++` as an underline, so read-only Markdown displays agree
 * with what the Markdown editor writes instead of showing the delimiters literally.
 */
export function remarkUnderline() {
  return (tree: MarkdownUnderlineNode, file: { value: unknown }): void =>
    replaceUnderlines(tree, typeof file.value === 'string' ? file.value : '');
}
